import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC } from 'react';
import { T } from 'src/shared/helpers';
import DragIcon from 'src/shared/material_icons/action/drag_indicator.svg';
import VisibilityIcon from 'src/shared/material_icons/action/visibility.svg';
import VisibilityOffIcon from 'src/shared/material_icons/action/visibility_off.svg';
import PlayIcon from 'src/shared/material_icons/av/play_arrow.svg';
import TrashIcon from 'src/shared/material_icons/action/delete.svg';
import styled, { css } from 'styled-components';
import { ComposerRowActionButton as ActionButton } from '../../../configurator-components/composer-editors/composerWorkspace';
import { getComposerActionDefinition } from '../../../configurator-components/composer-editors/attributes/composerActionCatalog';
import { ComposerOptionActionNode, ComposerOptionActionsType } from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { getComposerNodeValidationMessages } from '../helpers/composerAttributeValidation';
import ComposerValidationTooltip from './composerValidationTooltip';
import { ChevronIcon } from '../../../../../../widgets/icons/scene';
import { ComposerActionGlyph } from '../../../configurator-components/composer-editors/attributes/composerActionGlyph';
import { ComposerSlot } from '../../../configurator-components/composer-editors/composerSlots';
import { composerActionTypeOptions } from '../../../configurator-components/composer-editors/attributes/composerActionEditor';
import AddIcon from 'src/shared/material_icons/content/add.svg';

interface ComposerActionTreeElementProps {
    action: ComposerOptionActionNode;
    findMesh: (meshId: string) => BABYLON.AbstractMesh | undefined;
    findMaterial: (materialId: string) => BABYLON.Material | undefined;
    findCamera: (cameraId: string) => BABYLON.Camera | undefined;
    findPrintingMethod?: (printingMethodId: string) => { name?: string } | undefined;
    onSelect: (event: React.MouseEvent<HTMLDivElement>, action: ComposerOptionActionNode) => void;
    onPlay?: (action: ComposerOptionActionNode) => void;
    onRemove?: (action: ComposerOptionActionNode) => void;
    onVisibilityClick?: (action: ComposerOptionActionNode) => void;
    onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>, action: ComposerOptionActionNode) => void;
    onMoveUp?: (action: ComposerOptionActionNode) => void;
    onMoveDown?: (action: ComposerOptionActionNode) => void;
    /** La presa: il trascinamento lo conduce chi ospita le card (`startPointerReorder`); si sposta la card intera. */
    onGripPointerDown?: (event: React.PointerEvent<HTMLButtonElement>, action: ComposerOptionActionNode) => void;
    selected: boolean;
    editor?: React.ReactNode;
    /** Al posto del riassunto sotto il nome: «Mixed values» di un'azione condivisa da più opzioni. */
    caption?: string;
}

// L'AZIONE È UNA CARD (round 4, pacchetto I; utente, 2026-10-14): una testata che dice che cosa fa e su
// che cosa — «Zone 1 → Show» — e che, aperta, contiene il suo editor. Prima era una riga di tabella con
// l'editor appeso sotto: si leggeva come una cella, e non si capiva dove finisse l'azione aperta.
const Card = styled.div<{ $selected: boolean }>`
    min-width: 0;
    background: var(--composer-panel, #fff);
    border-radius: 8px;
    box-shadow: inset 0 0 0 1px ${(props) => (props.$selected ? 'var(--composer-accent, #e8650a)' : 'var(--composer-line, #dce3eb)')};
`;

const Row = styled.div<{ $selected: boolean; $muted: boolean }>`
    position: relative;
    display: flex;
    min-width: 0;
    min-height: 46px;
    align-items: center;
    gap: 6px;
    padding: 6px 6px 6px 4px;
    color: var(--composer-fg, #1e2a38);
    border-radius: 8px;
    cursor: pointer;
    outline: none;
    user-select: none;

    &:hover,
    &:focus-visible {
        background: ${(props) => (props.$selected ? 'transparent' : 'var(--composer-hover, #e9eff5)')};
    }

    /* A disabled action reads as spent, its own icon apart. */
    > div:not(:last-child) {
        opacity: ${(props) => (props.$muted ? 0.5 : 1)};
    }

    /* Row commands appear on hover, except the ones marked persistent. :focus-within alone would keep them lit
       after a plain click, since the row itself takes focus. */
    > div:last-child > *:not([data-persistent]) {
        opacity: 0;
        transition: opacity 110ms ease;
    }

    &:hover > div:last-child > *,
    &:focus-within:not(:focus) > div:last-child > * {
        opacity: 1;
    }

    /* La card aperta tiene i suoi comandi accesi: è quella su cui si sta lavorando. */
    ${(props) =>
        props.$selected &&
        css`
            > div:last-child > * {
                opacity: 1;
            }
        `}
`;

const Summary = styled.div`
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
    gap: 1px;
`;

// IL NOME DEL TIPO IN ARANCIO, COL SUO SEGNO (prototipo V2): la testata dice prima che cosa fa l'azione,
// sotto su che cosa.
const ActionName = styled.span`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 6px;
    overflow: hidden;
    color: var(--composer-accent-ink, #b4520f);
    font-size: 13px;
    font-weight: 600;
    line-height: 1.35;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ActionGlyph = styled.span`
    display: inline-flex;
    flex: none;

    svg {
        width: 15px;
        height: 15px;
        fill: currentColor;
    }
`;

const ActionTarget = styled.span`
    overflow: hidden;
    color: var(--composer-faint, #677078);
    font-size: 11px;
    line-height: 1.3;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 1px;
`;

const Chevron = styled.span<{ $open: boolean }>`
    display: inline-flex;
    color: var(--composer-faint, #677078);
    transform: rotate(${(props) => (props.$open ? '180deg' : '0deg')});
    transition: transform 120ms ease;

    svg {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

const UNDEFINED_PLACEHOLDER = '—';

export const ComposerActionTreeElement: FC<ComposerActionTreeElementProps> = ({
    action,
    onSelect,
    onDoubleClick,
    onPlay,
    onRemove,
    onVisibilityClick,
    findMesh,
    findMaterial,
    findCamera,
    findPrintingMethod,
    selected,
    onMoveUp,
    onMoveDown,
    onGripPointerDown,
    editor,
    caption
}) => {
    const enabled = action.enabled !== false;
    const aggregate = (find: (id: string) => { name?: string } | undefined, value: string) =>
        value
            .split(/[;,]/)
            .filter(Boolean)
            .map((id) => find(id.trim())?.name ?? id.trim())
            .join(', ');

    const [selector, value] = React.useMemo(() => {
        switch (action.type) {
            case ComposerOptionActionsType.SHOW_OBJECTS:
                return [aggregate(findMesh, action.selector), ''];
            case ComposerOptionActionsType.HIDE_OBJECTS:
                return [aggregate(findMesh, action.selector), ''];
            case ComposerOptionActionsType.SET_OBJECT_MATERIAL:
            case ComposerOptionActionsType.SET_SUB_OBJECT_MATERIAL:
                return [aggregate(findMesh, action.selector), aggregate(findMaterial, action.value)];
            case ComposerOptionActionsType.SET_MATERIAL_COLOR:
                return [aggregate(findMaterial, action.selector), action.value];
            case ComposerOptionActionsType.SET_MATERIAL_PARAMETER:
                return [aggregate(findMaterial, action.selector), `${action.property ?? ''}: ${action.value}`];
            case ComposerOptionActionsType.MOVE_CAMERA:
                return [UNDEFINED_PLACEHOLDER, aggregate(findCamera, action.selector || action.value)];
            case ComposerOptionActionsType.SET_DESIGN_MATERIAL:
                return [aggregate(findMesh, action.selector), aggregate(findMaterial, action.value)];
            case ComposerOptionActionsType.SET_CAMERA_PIVOT:
                return [UNDEFINED_PLACEHOLDER, aggregate(findCamera, action.value)];
            case ComposerOptionActionsType.RESET_CAMERA_PIVOT:
                return [UNDEFINED_PLACEHOLDER, UNDEFINED_PLACEHOLDER];
            case ComposerOptionActionsType.SET_PRINTING_METHOD:
                return [UNDEFINED_PLACEHOLDER, findPrintingMethod?.(action.value)?.name || action.value];
            default:
                return [action.selector, action.value];
        }
    }, [action, findCamera, findMaterial, findMesh, findPrintingMethod]);

    const definition = getComposerActionDefinition(action.type);
    const name = definition.name;
    const validationMessages = getComposerNodeValidationMessages(action);

    // «Zone 1 → Show»: su che cosa, poi che cosa. Chi ha una parte sola mostra quella.
    const summary = caption ?? [selector, value].filter((part) => part && part !== UNDEFINED_PLACEHOLDER).join(' → ');

    return (
        <Card $selected={selected} data-reorder-id={action.id} data-reorder-level={0}>
            <Row
                $selected={selected}
                $muted={!enabled}
                aria-selected={selected}
                aria-expanded={!!editor}
                onClick={(event) => onSelect(event, action)}
                onDoubleClick={onDoubleClick ? (event) => onDoubleClick(event, action) : undefined}
                onKeyDown={(event) => {
                    if (event.key === 'ArrowUp' && event.altKey) {
                        event.preventDefault();
                        onMoveUp?.(action);
                    }
                    if (event.key === 'ArrowDown' && event.altKey) {
                        event.preventDefault();
                        onMoveDown?.(action);
                    }
                }}
                role='row'
                tabIndex={0}
            >
                {onGripPointerDown && (
                    <ZTooltip title={T._('Drag to reorder · Alt+Arrow to move', 'Admin')}>
                        <ActionButton
                            data-persistent='true'
                            aria-label={T._('Drag to reorder', 'Admin')}
                            onPointerDown={(event) => onGripPointerDown(event, action)}
                        >
                            <DragIcon />
                        </ActionButton>
                    </ZTooltip>
                )}
                <Summary>
                    <ActionName title={name}>
                        <ActionGlyph aria-hidden='true'>
                            <ComposerActionGlyph icon={definition.icon} />
                        </ActionGlyph>
                        {name}
                        {validationMessages.length > 0 && <ComposerValidationTooltip messages={validationMessages} />}
                    </ActionName>
                    <ActionTarget title={summary || UNDEFINED_PLACEHOLDER}>{summary || UNDEFINED_PLACEHOLDER}</ActionTarget>
                </Summary>
                <Actions>
                    {onPlay && (
                        <ZTooltip title={T._('Run this action', 'Admin')}>
                            <ActionButton
                                aria-label={T._('Run this action', 'Admin')}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onPlay(action);
                                }}
                            >
                                <PlayIcon />
                            </ActionButton>
                        </ZTooltip>
                    )}
                    {onVisibilityClick && (
                        <ZTooltip title={enabled ? T._('Disable this action', 'Admin') : T._('Enable this action', 'Admin')}>
                            <ActionButton
                                $off={!enabled}
                                aria-label={enabled ? T._('Disable this action', 'Admin') : T._('Enable this action', 'Admin')}
                                aria-pressed={!enabled}
                                data-persistent={enabled ? undefined : 'true'}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onVisibilityClick(action);
                                }}
                            >
                                {enabled ? <VisibilityIcon /> : <VisibilityOffIcon />}
                            </ActionButton>
                        </ZTooltip>
                    )}
                    {onRemove && (
                        <ZTooltip title={T._('Remove action', 'Admin')}>
                            <ActionButton
                                $danger
                                aria-label={T._('Remove action', 'Admin')}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onRemove(action);
                                }}
                            >
                                <TrashIcon />
                            </ActionButton>
                        </ZTooltip>
                    )}
                    <Chevron $open={!!editor} data-persistent='true' aria-hidden='true'>
                        <ChevronIcon />
                    </Chevron>
                </Actions>
            </Row>
            {editor}
        </Card>
    );
};

// LA CARD CHE NASCE (prototipo V2, «Choose a rule…»): prima di avere un tipo un'azione non è niente, e la
// card chiede soltanto quello — l'elenco dei tipi, ognuno col suo segno e la sua descrizione, si apre da
// solo. Scelto il tipo, la card diventa l'azione. Il cestino la lascia perdere.
const DraftHead = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 6px;
    padding: 6px;
`;

export const ComposerActionDraftCard: FC<{ onChoose: (type: ComposerOptionActionsType) => void; onDiscard: () => void }> = ({ onChoose, onDiscard }) => {
    const slotRef = React.useRef<HTMLDivElement>(null);
    React.useEffect(() => {
        slotRef.current?.querySelector<HTMLButtonElement>('button')?.click();
    }, []);
    return (
        <Card $selected>
            <DraftHead>
                <div ref={slotRef}>
                    <ComposerSlot
                        id='composer-action-draft-type'
                        options={composerActionTypeOptions()}
                        glyph={<AddIcon />}
                        emptyLabel={T._('Choose an action…', 'Admin')}
                        searchPlaceholder={T._('Search actions…', 'Admin')}
                        onChange={(type) => onChoose(Number(type) as ComposerOptionActionsType)}
                    />
                </div>
                <ZTooltip title={T._('Discard this action', 'Admin')}>
                    <ActionButton $danger aria-label={T._('Discard this action', 'Admin')} onClick={onDiscard}>
                        <TrashIcon />
                    </ActionButton>
                </ZTooltip>
            </DraftHead>
        </Card>
    );
};
